// @flow
import React, { PureComponent } from 'react';
import { Redirect } from 'react-router-dom';
import { withHelmet } from 'infrastructure/seo/Helmet';
import type { ThemeType, QuestionType } from 'types/Faq';
import { Link } from 'react-router-dom';
import { handleTagEvent } from 'utils/tagManagerUtils';
import { getThemeWithQuestion } from 'utils/faqUtils';
import { getMetaBySlug } from 'utils/seoUtils';

export type StateProps = {
  themes?: Array<ThemeType>,
  questions: Array<QuestionType>
};

type Props = {
  match: {
    params: {
      slugTheme: string,
      slugQuestion: string
    }
  },
  validSearch: string
} & StateProps;

type State = {
  isDisplayed: boolean
};

class FaqContent extends PureComponent<Props, State> {
  state: State = {
    isDisplayed: true
  };

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevState.isDisplayed === this.state.isDisplayed) {
      this.setState({
        isDisplayed: true
      });
    }
  }

  renderQuestions = () => {
    const { questions, themes, validSearch } = this.props;
    const { slugQuestion, slugTheme } = this.props.match.params;

    if (!themes || themes.length === 0) {
      return null;
    }

    let filteredTheme = themes.filter(theme => theme.slug === slugTheme);
    if (!filteredTheme || (filteredTheme.length === 0 && validSearch === '')) {
      filteredTheme = getThemeWithQuestion(themes, questions);
    }

    const filteredQuestions = questions.filter(question => question.theme === filteredTheme[0].id);
    const slug = slugTheme || filteredTheme[0].slug;

    // https://www.codementor.io/blog/react-optimization-5wiwjnf9hj#9-avoid-using-index-as-key-for-map
    return filteredQuestions.map(question => {
      if (question.slug !== slugQuestion) {
        return (
          <Link
            key={question.slug}
            to={`/faq/${slug}/${question.slug}`}
            className="link-chevron link-chevron--din"
            data-label=""
          >
            <span>{question.title}</span>
            <i className="icon icon-chevron-right" />
          </Link>
        );
      }
      return null;
    });
  };

  renderQuestionsSearch = (questions: Array<QuestionType>): any => {
    const { themes } = this.props;
    if (!themes || themes.length === 0) {
      return null;
    }

    return questions.map(question => {
      const filteredTheme = themes.filter(theme => theme.id === question.theme);

      // https://www.codementor.io/blog/react-optimization-5wiwjnf9hj#9-avoid-using-index-as-key-for-map
      return (
        <Link
          key={question.slug}
          to={`/faq/${filteredTheme[0].slug}/${question.slug}`}
          className="link-chevron link-chevron--din"
          data-label=""
        >
          <span>{question.title}</span>
          <i className="icon icon-chevron-right" />
        </Link>
      );
    });
  };

  handledClickStatistics = () => {
    this.setState({
      isDisplayed: false
    });
  };

  renderMainContent = () => {
    const { validSearch } = this.props;
    const { slugTheme, slugQuestion } = this.props.match.params;

    if (validSearch) {
      const { questions } = this.props;
      const filteredQuestion = questions.filter(
        question =>
          question.title.toLowerCase().includes(validSearch.toLowerCase()) ||
          question.content.toLowerCase().includes(validSearch.toLowerCase())
      );

      if (filteredQuestion.length === 0) {
        return (
          <div className="block-faq__content js-nofind">
            <h2 className="mb-2">{`Aucun résultat pour « ${validSearch} »`}</h2>
            <p className="ft-secondary mb-6 is-grey">{"Nous n'avons trouvé aucun résultat pour votre recherche"}</p>
            <p className="ft-primary">Les questions les plus fréquentes</p>
            <div className="block-faq__links" />
          </div>
        );
      }

      return (
        <div className="block-faq__content js-find">
          <h2 className="mb-2">Resultats de la recherche</h2>
          <p className="ft-secondary mb-6 is-grey" />
          {this.renderQuestionsSearch(filteredQuestion)}
        </div>
      );
    }
    if (!slugQuestion) {
      const { themes, questions } = this.props;

      if (!themes || themes.length === 0) {
        return null;
      }

      let filteredTheme = themes.filter(theme => theme.slug === slugTheme);

      if (filteredTheme.length === 0) {
        filteredTheme = getThemeWithQuestion(themes, questions);
      }
      if (!filteredTheme[0]) {
        return null;
      }

      return (
        <div className="block-faq__content js-issues">
          <h2 className="mb-2">{filteredTheme[0].name}</h2>
          <p className="ft-secondary mb-4">{filteredTheme[0].description}</p>
          <div className="block-faq__links">{this.renderQuestions()}</div>
        </div>
      );
    } else {
      const { questions } = this.props;
      const { isDisplayed } = this.state;
      if (!questions || questions.length === 0) {
        return null;
      }
      const filteredQuestion = questions.filter(question => question.slug === slugQuestion);

      if (filteredQuestion.length < 1) {
        return <Redirect to="/404" />;
      }

      // TODO : Change to html generated with Contant @WAITING API
      return (
        <div className="block-faq__content js-find">
          <h2 className="mb-2">{filteredQuestion[0].title}</h2>
          <div className="ft-secondary mb-4" dangerouslySetInnerHTML={{ __html: filteredQuestion[0].content }} />
          {isDisplayed && (
            <p className="ft-secondary mb-4">
              Avez-vous trouvé cet article utile ?
              <span>
                <button
                  className="btn btn--white btn--xs"
                  onClick={handleTagEvent('faq', 'clic reponse faq', 'Yes', this.handledClickStatistics)}
                >
                  oui
                </button>
                {' ou '}
                <button
                  className="btn btn--white btn--xs"
                  onClick={handleTagEvent('faq', 'clic reponse faq', 'No', this.handledClickStatistics)}
                >
                  non
                </button>
              </span>
            </p>
          )}
          <p className="ft-h3">Sur le même sujet</p>
          <div className="block-faq__links">{this.renderQuestions()}</div>
        </div>
      );
    }
  };

  render() {
    return (
      <div className="col-md-8 col-lg-9">
        {this.renderMainContent()}
        <div className="block-faq__write">
          <h3 className="mb-1">Vous ne trouvez pas ce que vous cherchez ?</h3>
          <p className="mb-2">Nous pouvons vous aider à trouver une réponse</p>
          <Link to="/contact/nous-contacter" className="btn btn--primary">
            Contactez-nous
          </Link>
        </div>
      </div>
    );
  }
}

export default withHelmet(FaqContent)((Helmet, pageProps) => {
  if (pageProps.match.params.slugQuestion) {
    const question = pageProps.questions.find(question => pageProps.match.params.slugQuestion === question.slug);
    if (question) {
      return (
        <Helmet>
          <title>{question.meta_title}</title>
          <meta name={'description'} content={question.meta_description} />
          <meta property={'og:title'} content={question.meta_title} />
          <meta property={'og:description'} content={question.meta_description} />
        </Helmet>
      );
    }
  } else if (pageProps.match.params.slugTheme) {
    const meta = getMetaBySlug(`faq/slugTheme`);
    const theme = pageProps.themes.find(theme => pageProps.match.params.slugTheme === theme.slug);
    if (theme) {
      return (
        <Helmet>
          <title>{meta.title(theme.name)}</title>
          <meta name={'description'} content={meta.description(theme.name)} />
          <meta property={'og:title'} content={meta.title(theme.name)} />
          <meta property={'og:description'} content={meta.description(theme.name)} />
        </Helmet>
      );
    }
  }
});
